import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatDate } from "@/lib/plant";

export function PhotoLightbox({ photos, index, onClose, onIndex }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % photos.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, photos.length, onClose, onIndex]);

  if (index == null || !photos[index]) return null;
  const photo = photos[index];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="photo-lightbox">
        <DialogTitle className="sr-only">Photo viewer</DialogTitle>
        <div className="relative">
          <AuthImage path={photo.path} className="w-full max-h-[75vh] object-contain bg-black" />
          {photos.length > 1 && (
            <>
              <button onClick={() => onIndex((index - 1 + photos.length) % photos.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-2" data-testid="lightbox-prev">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button onClick={() => onIndex((index + 1) % photos.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-2" data-testid="lightbox-next">
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
          {(photo.date || photo.stage) && (
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white text-sm">
              {photo.stage && <span className="font-semibold">{photo.stage}</span>}
              {photo.date && <span className="text-white/70"> · {formatDate(photo.date)}</span>}
              <span className="float-right text-white/60">{index + 1} / {photos.length}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PlantGallery({ plantId, refreshKey }) {
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get(`/plants/${plantId}/gallery`).then(({ data }) => setPhotos(data.photos)).catch(() => setPhotos([]));
  }, [plantId, refreshKey]);

  if (photos.length === 0) return null;

  return (
    <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm" data-testid="plant-gallery">
      <h2 className="font-display text-lg font-semibold mb-4">Photo Story <span className="text-sm font-normal text-muted-foreground">({photos.length})</span></h2>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {photos.map((p, i) => (
          <button
            key={p.path}
            onClick={() => setActive(i)}
            className="shrink-0 w-24 h-24 rounded-xl overflow-hidden border border-border hover:ring-2 hover:ring-emerald-500 transition"
            data-testid="gallery-thumb"
          >
            <AuthImage path={p.path} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
      {active != null && <PhotoLightbox photos={photos} index={active} onClose={() => setActive(null)} onIndex={setActive} />}
    </div>
  );
}
